/**
 * PigmentMinQtyValidator — v3
 */
class PigmentMinQtyValidator {
  // ─── Variant ID → minimum quantity ────────────────────────────
  VARIANT_MINIMUMS = {
    // '53234352521581': 50,  // Pigment 201 — 1 lb
    '53234353406317': 10,  // Pigment 201 — 5 lb
    // '53234354291053':  2,  // Pigment 201 — 25 lb
    // '53234352587117': 50,  // Pigment 203 — 1 lb
    '53234353471853': 10,  // Pigment 203 — 5 lb
    // '53234354356589':  2, // Pigment 203 — 25 lb
    '53137500537197': 10, // Shade Picker — 201-1 — 5 lb
    '53137514299757':  2, // Shade Picker — 201-1 — 25 lb
    '53137500569965': 10, // Shade Picker — 201-3 — 5 lb
    '53137514332525': 2,  // Shade Picker — 201-3 — 25 lb
    '53137500602733': 10, // Shade Picker — 201-5 — 5 lb
    '53137514365293': 2,  // Shade Picker — 201-5 — 25 lb
    '53137500635501': 10, // Shade Picker — 201-7 — 5 lb
    '53137514398061': 2,  // Shade Picker — 201-7 — 25 lb
    '53137500799341': 10, // Shade Picker — 203-1 — 5 lb
    '53137514561901': 2,  // Shade Picker — 203-1 — 25 lb
    '53137500832109': 10, // Shade Picker — 203-3 — 5 lb
    '53137514594669': 2,  // Shade Picker — 203-3 — 25 lb
    '53137500864877': 10, // Shade Picker — 203-5 — 5 lb
    '53137514627437': 2,  // Shade Picker — 203-5 — 25 lb
    '53137500897645': 10, // Shade Picker — 203-7 — 5 lb
    '53137514660205': 2,  // Shade Picker — 203-7 — 25 lb
  };
  // ──────────────────────────────────────────────────────────────

  constructor() {
    this.injectStyles();
    // Capture phase = runs BEFORE the theme's handlers
    document.addEventListener('click',  this.onMinusClick.bind(this), true);
    document.addEventListener('change', this.onQtyChange.bind(this),  true);
    // Safety net for any edge cases
    document.addEventListener('cart:updated', this.onCartUpdated.bind(this));
    this.validateOnLoad();
  }

  // ── Helpers ───────────────────────────────────────────────────

  isInCart(el) {
    return !!el.closest('cart-drawer, cart-items');
  }

  // Confirmed selector: input.quantity__input with data-quantity-variant-id
  getQtyInput(el) {
    return el.closest('quantity-input, .quantity')
      ?.querySelector('input.quantity__input[data-quantity-variant-id]')
      || el.closest('quantity-input, .quantity')
      ?.querySelector('input.quantity__input')
      || null;
  }

  getVariantId(el) {
    // Primary: data-quantity-variant-id on the input
    const qtyInput = this.getQtyInput(el);
    if (qtyInput?.dataset.quantityVariantId) return qtyInput.dataset.quantityVariantId;
    // Fallback: data-variant-id on the cart item <li>
    return el.closest('[data-variant-id]')?.dataset.variantId || null;
  }

  getMin(variantId) {
    return this.VARIANT_MINIMUMS[String(variantId)] ?? null;
  }

  // ── Intercept minus click (capture phase) ─────────────────────
  onMinusClick(e) {
    const btn = e.target.closest('[name="minus"]');
    if (!btn || !this.isInCart(btn)) return;

    const variantId = this.getVariantId(btn);
    if (!variantId) return;

    const min = this.getMin(variantId);
    if (min === null) return;

    const qtyInput = this.getQtyInput(btn);
    const current  = parseInt(qtyInput?.value) || 0;

    if (current <= min) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.flashMin(qtyInput, min);
    }
  }

  // ── Intercept manual qty input (capture phase) ────────────────
  onQtyChange(e) {
    const input = e.target;
    if (!input.classList.contains('quantity__input') || !this.isInCart(input)) return;

    const variantId = input.dataset.quantityVariantId;
    const min       = this.getMin(variantId);
    if (min !== null && parseInt(input.value) < min) {
      e.preventDefault();
      e.stopImmediatePropagation();
      input.value = min;
    }
  }

  // ── Safety net ────────────────────────────────────────────────
  async validateOnLoad() {
    this.blockCheckout();
    await new Promise(r => setTimeout(r, 300));
    const res  = await fetch(FoxTheme.routes.cart_url, FoxTheme.utils.fetchConfig());
    const cart = await res.json();
    await this.enforce(cart);
    this.unblockCheckout();
    this.renderPermanentLabels(cart);
  }

  onCartUpdated(event) {
    if (event.detail?.cart) {
      this.blockCheckout();
      this.enforce(event.detail.cart).then(() => {
        this.unblockCheckout();
        this.renderPermanentLabels(event.detail.cart);
      });
    }
  }

  // ── Block / unblock checkout (same pattern as BundleCartValidator) ──
  blockCheckout() {
    document.querySelectorAll('[name="checkout"], a[href="/checkout"]').forEach(btn => {
      btn.setAttribute('disabled', true);
      btn.setAttribute('aria-disabled', 'true');
      btn.dataset.pigmentValidating = 'true';
    });
  }

  unblockCheckout() {
    document.querySelectorAll('[data-pigment-validating]').forEach(btn => {
      btn.removeAttribute('data-pigment-validating');
      // Only re-enable if BundleCartValidator is also done
      if (!btn.hasAttribute('data-bundle-validating')) {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
      }
    });
  }

  async enforce(cart) {
    if (this._enforcing) return;
    if (!cart.items?.length) return;
    const toFix = cart.items.filter(item => {
      const min = this.VARIANT_MINIMUMS[String(item.variant_id)];
      return min !== undefined && item.quantity < min;
    });
    if (!toFix.length) return;

    this._enforcing = true;
    for (const item of toFix) {
      const min = this.VARIANT_MINIMUMS[String(item.variant_id)];
      let sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
      );
      const res = await fetch(FoxTheme.routes.cart_change_url, {
        ...FoxTheme.utils.fetchConfig(),
        body: JSON.stringify({ id: item.key, quantity: min, sections }),
      });
      const updated = await res.json();
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: updated });
    }
    this._enforcing = false;
    this.showInlineLabels(toFix);
  }

  // ── Visual feedback on blocked minus ─────────────────────────
  flashMin(qtyInput, min) {
    if (!qtyInput) return;

    // Shake animation
    qtyInput.animate?.(
      [{ transform: 'translateX(-3px)' }, { transform: 'translateX(3px)' },
       { transform: 'translateX(-3px)' }, { transform: 'translateX(0)' }],
      { duration: 280, easing: 'ease-in-out' }
    );

    // Remove any existing label
    document.querySelectorAll('.pmv-min-label').forEach(el => el.remove());

    const wrapper = qtyInput.closest('quantity-input, .quantity');
    if (!wrapper) return;

    // Position relative on wrapper so the label can anchor absolutely below it
    wrapper.style.position = 'relative';

    const label = document.createElement('span');
    label.className = 'pmv-min-label';
    label.textContent = 'Minimum: ' + min + ' units';
    label.style.cssText = [
      'position:absolute',
      'top:calc(100% + 4px)',
      'left:55%',
      'transform:translateX(-45%)',
      'font-size:12px',
      'color:#b45309',
      'font-weight:600',
      'white-space:nowrap',
      'z-index:1',
    ].join(';');

    wrapper.appendChild(label);
    setTimeout(() => label.remove(), 2000);
  }

  // ── Toast ─────────────────────────────────────────────────────
  injectStyles() { /* no styles needed */ }

  // ── Permanent labels (always visible for restricted items) ──────
  renderPermanentLabels(cart) {
    // Clear all existing permanent labels in both cart contexts
    document.querySelectorAll('.pmv-min-label--permanent').forEach(el => el.remove());

    if (!cart.items?.length) return;

    // Search inside cart drawer AND cart page
    const cartRoots = [
      ...document.querySelectorAll('cart-drawer'),
      ...document.querySelectorAll('cart-items'),
    ];
    if (!cartRoots.length) return;

    cart.items.forEach(item => {
      const min = this.VARIANT_MINIMUMS[String(item.variant_id)];
      if (min === undefined) return;

      cartRoots.forEach(root => {
        // querySelectorAll in case the same variant appears multiple times
        root.querySelectorAll(
          `input.quantity__input[data-quantity-variant-id="${item.variant_id}"]`
        ).forEach(qtyInput => this.renderMinLabel(qtyInput, min));
      });
    });
  }

  renderMinLabel(qtyInput, min) {
    const wrapper = qtyInput.closest('quantity-input, .quantity');
    if (!wrapper) return;

    // Remove any existing permanent label for this input
    wrapper.querySelector('.pmv-min-label--permanent')?.remove();

    wrapper.style.position = 'relative';

    const label = document.createElement('span');
    label.className = 'pmv-min-label--permanent';
    label.textContent = 'Minimum: ' + min + ' units';
    label.style.cssText = [
      'position:absolute',
      'top:calc(100% + 4px)',
      'left:55%',
      'transform:translateX(-45%)',
      'font-size:12px',
      'color:#b45309',
      'font-weight:600',
      'white-space:nowrap',
      'z-index:1',
    ].join(';');

    wrapper.appendChild(label);
  }

  showInlineLabels(items) {
    const cartRoots = [
      ...document.querySelectorAll('cart-drawer'),
      ...document.querySelectorAll('cart-items'),
    ];
    if (!cartRoots.length) return;

    items.forEach(item => {
      const min = this.VARIANT_MINIMUMS[String(item.variant_id)];
      cartRoots.forEach(root => {
        root.querySelectorAll(
          `input.quantity__input[data-quantity-variant-id="${item.variant_id}"]`
        ).forEach(qtyInput => this.flashMin(qtyInput, min));
      });
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (window.location.pathname.includes('/cart') || document.querySelector('cart-drawer')) {
    new PigmentMinQtyValidator();
  }
});